import { supabase } from './supabase';
import type { Font, FieldFont } from '@jigflow/shared';

export async function getFonts(workspaceId: string): Promise<Font[]> {
  const { data, error } = await supabase
    .from('fonts')
    .select('*')
    .eq('workspace_id', workspaceId);

  if (error) throw error;
  if (!data) return [];

  return data.map((row) => ({
    id: row.id,
    name: row.name,
    family: row.family,
    url: row.storage_path ? getFontUrl(row.storage_path) : undefined,
    isSystem: false,
    category: row.category || undefined,
    style: row.style || undefined,
  }));
}

export async function getFieldFonts(fieldId: string): Promise<FieldFont[]> {
  const { data, error } = await supabase
    .from('field_fonts')
    .select('*, fonts(*)')
    .eq('field_id', fieldId);

  if (error) throw error;
  if (!data) return [];

  return data.map((row) => ({
    id: row.fonts.id,
    name: row.fonts.name,
    family: row.fonts.family,
    url: row.fonts.storage_path ? getFontUrl(row.fonts.storage_path) : undefined,
    isSystem: false,
    category: row.fonts.category || undefined,
    style: row.fonts.style || undefined,
    isPrimary: row.is_primary || false,
  }));
}

export async function uploadFont(
  workspaceId: string,
  file: File,
  name: string,
  family: string
): Promise<Font> {
  const fontId = crypto.randomUUID();
  const ext = file.name.split('.').pop()?.toLowerCase() || 'ttf';
  const storagePath = `fonts/${workspaceId}/${fontId}.${ext}`;

  // Map font extensions to proper MIME types (browsers often send application/octet-stream)
  const mimeMap: Record<string, string> = {
    ttf: 'font/ttf',
    otf: 'font/otf',
    woff: 'font/woff',
    woff2: 'font/woff2',
  };

  const { error: uploadError } = await supabase.storage
    .from('fonts')
    .upload(storagePath, file, {
      contentType: mimeMap[ext] || 'application/octet-stream',
    });

  if (uploadError) throw uploadError;

  const { data, error: insertError } = await supabase
    .from('fonts')
    .insert({
      id: fontId,
      workspace_id: workspaceId,
      name,
      family,
      storage_path: storagePath,
    })
    .select()
    .single();

  if (insertError) throw insertError;

  return {
    id: data.id,
    name: data.name,
    family: data.family,
    url: getFontUrl(data.storage_path),
    isSystem: false,
    category: data.category || undefined,
    style: data.style || undefined,
  };
}

export async function deleteFont(id: string): Promise<void> {
  const { data: font, error: fetchError } = await supabase
    .from('fonts')
    .select('storage_path')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  if (font.storage_path) {
    const { error: storageError } = await supabase.storage
      .from('fonts')
      .remove([font.storage_path]);

    if (storageError) throw storageError;
  }

  const { error: deleteError } = await supabase
    .from('fonts')
    .delete()
    .eq('id', id);

  if (deleteError) throw deleteError;
}

export function getFontUrl(storagePath: string): string {
  const { data } = supabase.storage.from('fonts').getPublicUrl(storagePath);
  return data.publicUrl;
}

export async function addFieldFont(
  workspaceId: string,
  fieldId: string,
  fontId: string,
  isPrimary: boolean = false
): Promise<void> {
  // If setting as primary, clear existing primaries first
  if (isPrimary) {
    await supabase
      .from('field_fonts')
      .update({ is_primary: false })
      .eq('field_id', fieldId);
  }

  const { error } = await supabase
    .from('field_fonts')
    .insert({
      workspace_id: workspaceId,
      field_id: fieldId,
      font_id: fontId,
      is_primary: isPrimary,
    });

  if (error) throw error;
}

export async function removeFieldFont(
  fieldId: string,
  fontId: string
): Promise<void> {
  const { error } = await supabase
    .from('field_fonts')
    .delete()
    .eq('field_id', fieldId)
    .eq('font_id', fontId);

  if (error) throw error;
}

export async function setFieldPrimaryFont(
  fieldId: string,
  fontId: string
): Promise<void> {
  // Clear all primaries for this field
  await supabase
    .from('field_fonts')
    .update({ is_primary: false })
    .eq('field_id', fieldId);

  // Set the new primary
  const { error } = await supabase
    .from('field_fonts')
    .update({ is_primary: true })
    .eq('field_id', fieldId)
    .eq('font_id', fontId);

  if (error) throw error;
}
